// Public storefront page. Two addressing modes share this one component:
//   • slug mode   — /g/:orgSlug and /g/:orgSlug/:slug (org param IS the slug)
//   • domain mode — a verified custom domain serving the site at "/" and "/:slug";
//     orgSlug comes from the host via the `domainOrgSlug` prop, not the URL, and
//     in-site navigation drops the /g/:orgSlug prefix.
//
// Resolve order: published builder page for the slug → render it; else a product
// handle in the org's catalog (template fallback) → render it; else 404. Renders
// the data-driven SiteTopBar (nav + cart + user pill) then the PageDoc via
// PageRenderer. Permitted users (admin / web-design editor) instead get the
// in-place SiteEditor (slide-out tabs over the site) — no separate route. The
// user menu and the editor live in a lazy account layer (SiteAccountSlot), so a
// visitor's page never loads auth, Matrix or the app store.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BootSplash } from '@/components/BootSplash';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditorFonts } from '@/utils/editorFonts';
import { PageRenderer } from './PageRenderer';
import { SiteTopBar } from './SiteTopBar';
import { siteShellStyle } from './leafWebStyles';
import { SiteAccountLoader, SiteAccountSlot } from './SiteAccountSlot';
import { useSiteAccount } from './siteAccountBridge';
import { pageUrl } from './pageUrl';
import { buildSlugByProductId } from './productSlug';
import { fetchPublicPage, fetchPublicProductPage, fetchPublicNav, fetchPublicFooter, fetchPublicTheme, fetchPublicProductPages, fetchPublicPopup, fetchPublicProductGalleries, type PopupConfig, type ProductGalleryConfig, type ProductPageRef } from '@/services/pageBuilderService';
import { NewsletterPromoPopup } from '@/features/newsletter/NewsletterPromoPopup';
import { setSiteBrandLogo } from '@/services/siteBoot';
import { createDefaultNav, navSplashLogo, withSharedFooter, withSharedTheme, type PageDoc, type SiteNav, type SectionBlock, type PageTheme } from '../../../shared/pageBlocks';
import { CartPill, fetchAllProducts, loadStorefrontConfig, type StoreProduct } from '@/features/store';

type Status = 'loading' | 'ready' | 'notfound';

const NotFound: React.FC = () => (
  <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
    <h1 style={{ fontSize: 48, margin: 0 }}>404</h1>
    <p style={{ opacity: 0.7 }}>This page doesn’t exist.</p>
  </div>
);

export const SitePage: React.FC<{ domainOrgSlug?: string }> = ({ domainOrgSlug }) => {
  const params = useParams<{ orgSlug?: string; slug?: string }>();
  const domainMode = !!domainOrgSlug;
  const orgSlug = domainOrgSlug || params.orgSlug || '';
  const slug = params.slug;
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [doc, setDoc] = useState<PageDoc | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [nav, setNav] = useState<SiteNav | null>(null);
  const [footer, setFooter] = useState<SectionBlock | null>(null);
  const [theme, setTheme] = useState<PageTheme | null>(null);
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [productPages, setProductPages] = useState<ProductPageRef[]>([]);
  const [productGalleries, setProductGalleries] = useState<Record<string, ProductGalleryConfig>>({});
  const [popup, setPopup] = useState<PopupConfig | null>(null);
  const { editing } = useSiteAccount();

  const pageSlug = (slug || 'home').toLowerCase();
  // Tab title mirrors the server-injected <title> (server/siteSeo.js): brand
  // alone on the landing page, "Page — Brand" on sub/product pages. Keeping the
  // rendered DOM in sync with the server head matters for SEO — Google indexes
  // the rendered title, so a brand-only overwrite here would collapse every
  // page to one title in search results.
  useEffect(() => {
    // Until the nav loads, keep the server-injected <title> — it already has
    // the right org brand; a premature fallback would stomp it with the wrong one.
    if (!nav) return;
    const brand = nav.brandName?.trim() || 'Sleepy Circuits';
    if (pageSlug === 'home') { document.title = brand; return; }
    // Until the page data loads, keep the server-injected <title> too — same
    // reasoning. `pageTitle` comes straight from the public page API, which
    // resolves the SAME real Shopify title server/siteSeo.js used for the SSR
    // <title> (see resolvePublicTitle in server/pageRoutes.js). Re-deriving it
    // here from the separately-fetched `products` list used to race that
    // resolution and could regress the tab to the raw handle when it lost.
    if (pageTitle == null) return;
    document.title = `${pageTitle} — ${brand}`;
  }, [nav?.brandName, pageSlug, pageTitle]);
  // Each page acts as a fresh page — navigating (e.g. "Learn more" from a grid)
  // must land at the TOP, not wherever the previous page was scrolled.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [pageSlug]);
  // Loading spinners show this site's nav logo while it is on screen; leaving the site restores the Sleepy logo.
  useEffect(() => { if (nav) setSiteBrandLogo(navSplashLogo(nav)); }, [nav]);
  useEffect(() => () => setSiteBrandLogo(null), []);
  // The site themes' web fonts (Quicksand, Comfortaa): site.html links them for a custom domain; inside the app
  // (/g/:orgSlug) nothing else does, and the theme would fall back to its email stack.
  useEditorFonts();

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    setDoc(null);
    setPageTitle(null);

    const ready = (d: PageDoc, title?: string | null) => {
      if (!alive) return;
      setDoc(d);
      setPageTitle(title?.trim() || null);
      setStatus('ready');
    };

    (async () => {
      // The page and its chrome do not wait on the Shopify config: they start
      // now, in parallel; only the product catalog needs the config first.
      const configReady = loadStorefrontConfig(orgSlug).catch(() => false);
      fetchPublicNav(orgSlug).then((n) => { if (alive) setNav(n || createDefaultNav()); });
      fetchPublicFooter(orgSlug).then((f) => { if (alive) setFooter(f); });
      fetchPublicTheme(orgSlug).then((t) => { if (alive) setTheme(t); });
      const catalog = configReady.then(() => fetchAllProducts());
      catalog.then((p) => { if (alive) setProducts(p); }).catch(() => undefined);
      fetchPublicProductPages(orgSlug).then((pp) => { if (alive) setProductPages(pp); });
      fetchPublicProductGalleries(orgSlug).then((g) => { if (alive) setProductGalleries(g); });
      fetchPublicPopup(orgSlug).then(({ popup: p }) => { if (alive) setPopup(p); });

      const page = await fetchPublicPage(orgSlug, pageSlug);
      if (!alive) return;
      if (page?.doc) return ready(page.doc, page.title);

      if (pageSlug !== 'home') {
        const prods = await catalog.catch(() => []);
        if (!alive) return;
        if (prods.some((p) => p.handle === pageSlug)) {
          const product = await fetchPublicProductPage(orgSlug, pageSlug);
          if (!alive) return;
          if (product?.doc) return ready(product.doc, product.title);
        }
      }
      setStatus('notfound');
    })();

    return () => { alive = false; };
  }, [orgSlug, pageSlug]);

  // Product (stable id) → internal page slug. Keyed on the product ID, never the
  // handle — handles are unreliable (they change on Shopify). Grids sort "opened"
  // products to the top by the same id set.
  const slugByProductId = useMemo(() => buildSlugByProductId(productPages, products), [productPages, products]);
  const productPageHandles = useMemo(() => new Set(slugByProductId.keys()), [slugByProductId]);

  const onNavigate = useCallback((target: string) => {
    if (/^https?:\/\//i.test(target)) { window.open(target, '_blank'); return; }
    // Product clicks carry the product's stable id → resolve to our internal slug.
    // Never emit a raw product id as a URL (that's the "gid://… unavailable" bug):
    // an unresolved id (page not yet published/backfilled) navigates nowhere.
    if (target.startsWith('gid://')) {
      const slug = slugByProductId.get(target);
      if (slug) navigate(pageUrl(orgSlug, domainMode, slug));
      return;
    }
    // Everything else is already an internal slug ('home', a page slug).
    navigate(pageUrl(orgSlug, domainMode, target));
  }, [navigate, orgSlug, domainMode, slugByProductId]);

  // Permitted users edit in place — the editor (drawn by the account layer) IS
  // the slide-out tabs over the site, so the visitor's page steps aside for it.
  // The account layer's loader stays at ONE place in the tree either way: a
  // second copy in an editing-only branch remounted the layer whenever editing
  // flipped on, its cleanup flipped it off again, and an editor's page looped
  // (re-running sign-in and reconnecting sync) forever.
  return (
    <>
    {!editing && (
    <div ref={scrollRef} style={{ height: '100dvh', overflowY: 'auto', background: theme?.pageBgColor || '#0a0a0a', color: '#e4e4e7' }}>
      {/* The shell caps the WHOLE site — nav + content + footer — at the site max
          width and centers it, so the page background shows on either side on
          wide screens. */}
      <div style={siteShellStyle(theme)}>
        <SiteTopBar
          nav={nav || createDefaultNav()}
          products={products}
          productPageHandles={productPageHandles}
          onNavigate={onNavigate}
          onBrand={() => onNavigate('home')}
          right={({ compact }) => <><CartPill /><SiteAccountSlot compact={compact} /></>}
        />
        {status === 'loading' && <BootSplash />}
        {status === 'notfound' && <NotFound />}
        {status === 'ready' && doc && <PageRenderer doc={withSharedTheme(withSharedFooter(doc, footer), theme)} onProductClick={(h) => onNavigate(h)} productPageHandles={productPageHandles} productGalleries={productGalleries} />}
      </div>
      {popup && <NewsletterPromoPopup orgSlug={orgSlug} config={popup} />}
    </div>
    )}
    <SiteAccountLoader pageReady={editing || status !== 'loading'} orgSlug={orgSlug} slug={slug} domainOrgSlug={domainOrgSlug} />
    </>
  );
};

export default SitePage;
